import React, { useCallback, useState } from 'react';
import {
  ButtonsContainer,
  TitleAndSubtitle,
  ContentButtonsWrapper,
} from '../../../layout/layout-container.styled';
import { NavButton } from '../../../nav-button/nav-button';
import { Text, font } from '../../../../style-utils/text';
import { VisitorLogState } from '../../../hooks/use-visitor-log-handler';
import { DefaultComponentProps } from '../../../app-router/component-props';
import { palette, spacing } from '../../../../style-utils/theme';
import { down } from '../../../../style-utils/media-queries';
import { UnitSelect } from '../../../capture/unit-select';
import { PersonField } from '../../../capture/person-field';
import styled from '@emotion/styled';
import { WalkInPassBanner } from './walk-in-pass-banner';

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${spacing.md};
  width: 70%;
  margin: 0 auto;

  /* down('md') is 767.98px. The hand-written 768px was inclusive, so at exactly
     768 both this rule and every up('md') rule applied at once — a one-pixel
     band where the layout disagreed with itself. */
  ${down('md')} {
    width: 100%;
  }
`;

// "Who are they visiting" used to be a free-text apartment number and nothing
// else. Two changes here, and they compound:
//
//   1. the apartment is now a DROPDOWN of the building's real units, which
//      fills the unit_id this table has always carried and never populated;
//   2. the host is now a PERSON, resolved against that unit's occupants.
//
// (2) is only cheap because of (1): choosing a unit usually narrows the roster
// to one resident, and then autocapture fills the host in with no interaction
// at all. A typed apartment number never could.
export const Location: React.FC<DefaultComponentProps> = ({
  navigate,
  changeHandler,
  state,
  nextPath,
  prevPath,
  setCapture,
  selectedOption,
}) => {
  // THIS STEP IS SHARED BY BOTH WIZARDS (resident-routes.tsx puts it on the
  // daily log too), and they resolve a person in different places:
  //
  //   visitor log  the person is the HOST, and this is the only step that can
  //                ask about them — the guest names are outsiders.
  //   daily log    the person is the SUBJECT, already resolved two steps back
  //                on NameAndLastName. A second picker here would offer to
  //                link a different resident to the same entry.
  //
  // So the host field renders on the visitor log only. The unit dropdown
  // renders on both — it is what feeds autocapture either way.
  const isVisitorLog = selectedOption === '/visitor-log';

  const {
    apartmentLocation,
    property_ids,
    unit_id,
    host_name,
    host_occupancy_id,
    host_resolution,
    names,
    lastNames,
  } = state as VisitorLogState;

  const buildingId = property_ids?.[0] ?? null;

  // Reported up from PersonField — only it knows the candidate count. See the
  // note on onRequirePickChange.
  const [mustPick, setMustPick] = useState(false);
  const handleRequirePick = useCallback((v: boolean) => setMustPick(v), []);

  return (
    <>
      <TitleAndSubtitle direction="column">
        <Text as="h1" fontSize={font.size.xl} fontWeight={700} mt={0} mb={8}>
          Apartment/Location
        </Text>
        <Text color={font.color.gray} fontSize={font.size.xs} mt={0} mb={0}>
          {`Choose the apartment, and who the visitor is here to see`}
        </Text>
      </TitleAndSubtitle>
      <ContentButtonsWrapper>
        <Stack>
          <UnitSelect
            value={apartmentLocation}
            buildingId={buildingId}
            unitId={unit_id}
            onChange={(label, id) => {
              changeHandler(label, 'apartmentLocation');
              // Changing the unit invalidates a person resolved against the old
              // one: whoever was auto-matched as "the only resident of 4B" is
              // not the answer once the unit becomes 9C. Clearing it lets
              // autocapture re-run instead of silently keeping a link the form
              // no longer supports.
              //
              // Cleared on BOTH wizards, under their own column names — the
              // daily log's subject was resolved on an earlier step and is just
              // as invalidated by a unit change as the visitor log's host.
              setCapture?.(
                isVisitorLog
                  ? { unit_id: id, host_occupancy_id: null, host_resolution: 'unset' }
                  : { unit_id: id, subject_occupancy_id: null, subject_resolution: 'unset' }
              );
            }}
          />
          {/* WP-4.4. Optional by design: a delivery, or a contractor visiting
              nobody in particular, is an ordinary visitor-log entry, and making
              this mandatory would block the busiest form in the product on a
              field that is often genuinely empty. The backend agrees —
              `required: false` on the host in the visitor-log router. */}
          {isVisitorLog && (
          <PersonField
            label="Visiting (optional)"
            value={host_name ?? ''}
            onChange={next => setCapture?.({ host_name: next })}
            resolves
            buildingId={buildingId}
            unitId={unit_id}
            resolution={host_resolution}
            occupancyId={host_occupancy_id}
            onResolve={picked =>
              setCapture?.({
                host_occupancy_id: picked.occupancy_id,
                host_resolution: picked.resolution,
              })
            }
            onRequirePickChange={handleRequirePick}
            placeholder="Which resident are they visiting?"
          />
          )}
          {/* RESIDENT-PORTAL PLAN 3 (RP-3.9): the typed visitor may already have
              a pass for this unit. Visitor log only — a daily-log subject is
              a resident, not a visitor. Never blocks Continue. */}
          {isVisitorLog && (
            <WalkInPassBanner
              buildingId={buildingId}
              unitId={unit_id ?? null}
              unitLabel={apartmentLocation}
              names={names || []}
              lastNames={lastNames || []}
              onUse={href => navigate(href, { replace: false })}
            />
          )}
        </Stack>
      </ContentButtonsWrapper>
      <ButtonsContainer>
        <NavButton
          disabled={false}
          onClick={() => navigate(prevPath, { replace: false })}
          direction="back"
          textColor={palette.slate}
          background="transparent"
          text={'Back'}
        />
        <NavButton
          // The apartment is still required; the host is not. But if a host
          // name WAS entered and it matches two residents, that ambiguity has
          // to be settled before moving on — the server would refuse the save
          // anyway.
          disabled={apartmentLocation?.length >= 1 && !mustPick ? false : true}
          direction="forward"
          text={'Continue'}
          onClick={() => navigate(nextPath, { replace: false })}
        />
      </ButtonsContainer>
    </>
  );
};
